import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import HaspLine from '../Hasp/HaspLine';
import HaspObject from '../Hasp/HaspObject';

const LineDrawingMixin = {
    enterLineDrawing(container) {
        this.lineDrawingMode = true;
        this.lineDrawingPoints = [];
        this.lineDrawingContainer = container;
        this._lastLineClickTime = 0;

        const stage = this.getStage();
        if (stage) stage.container().style.cursor = 'crosshair';

        // Committed segments — solid white line matching the final HaspLine style
        this.lineDrawingLine = new Konva.Line({
            id: uuidv4(),
            points: [],
            stroke: '#ffffff',
            strokeWidth: 2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
            visible: false,
        });
        this.add(this.lineDrawingLine);

        // Handle circles for each committed point
        this.lineDrawingHandlesGroup = new Konva.Group({ listening: false });
        this.add(this.lineDrawingHandlesGroup);

        // Dashed blue segment from last committed point to the mouse cursor
        this.linePreviewLine = new Konva.Line({
            id: uuidv4(),
            points: [],
            stroke: '#60A5FA',
            strokeWidth: 1,
            dash: [4, 4],
            listening: false,
            visible: false,
        });
        this.add(this.linePreviewLine);

        // ESC cancels drawing
        this._lineDrawEscHandler = (e) => {
            if (e.key === 'Escape') this.cancelLineDrawing();
        };
        document.addEventListener('keydown', this._lineDrawEscHandler);

        this.layerCallback('line-drawing-start', null);
    },

    _snapDrawPoint(cx, cy) {
        // Snap to grid as baseline
        let sx = Math.round(cx / this.gridSize) * this.gridSize;
        let sy = Math.round(cy / this.gridSize) * this.gridSize;

        // Override with guideline/object snap (red guides)
        let objR = null;
        const stage = this.getStage();
        if (stage && stage.snapManager && this.haspobject) {
            try {
                const targets = stage.snapManager.collectSnapTargets(this.haspobject, stage.guidelineManager);
                objR = stage.snapManager.findSnapForContentPos(
                    { x: cx, y: cy }, { width: 0, height: 0 }, targets
                );
                sx = objR.snapX;
                sy = objR.snapY;
            } catch (_) { /* no snapManager or no parent yet */ }
        }

        // Own committed-handle snap (yellow) — takes priority over object snap
        const SNAP_DIST = 8;
        let ownGuideX, ownGuideY, ownSnapX = cx, ownSnapY = cy;
        let ownMatchY = cy, ownMatchX = cx;
        for (let i = 0; i < this.lineDrawingPoints.length; i += 2) {
            const px = this.lineDrawingPoints[i];
            const py = this.lineDrawingPoints[i + 1];
            if (Math.abs(cx - px) <= SNAP_DIST) {
                ownGuideX = px; ownSnapX = px; ownMatchY = py;
            }
            if (Math.abs(cy - py) <= SNAP_DIST) {
                ownGuideY = py; ownSnapY = py; ownMatchX = px;
            }
        }

        const useOwn = ownGuideX !== undefined || ownGuideY !== undefined;

        // Segment midpoint snap (yellow perpendicular).
        // Triggers when cursor is within SNAP_DIST of the perpendicular line through the midpoint.
        // Snaps the along-segment component to the midpoint so the guide reaches the cursor.
        let midSnap = null;
        for (let i = 0; i < this.lineDrawingPoints.length - 2; i += 2) {
            const ax = this.lineDrawingPoints[i],     ay = this.lineDrawingPoints[i + 1];
            const bx = this.lineDrawingPoints[i + 2], by = this.lineDrawingPoints[i + 3];
            const mx = (ax + bx) / 2, my = (ay + by) / 2;
            const segLen = Math.hypot(bx - ax, by - ay);
            if (segLen === 0) continue;
            const tx = (bx - ax) / segLen, ty = (by - ay) / segLen;
            const cmx = cx - mx, cmy = cy - my;
            const alongSeg = cmx * tx + cmy * ty;
            const d = Math.abs(alongSeg);
            if (d < SNAP_DIST && (!midSnap || d < midSnap.dist)) {
                midSnap = {
                    snapX: cx - alongSeg * tx,
                    snapY: cy - alongSeg * ty,
                    midX: mx, midY: my,
                    segDx: bx - ax, segDy: by - ay, dist: d,
                };
            }
        }

        // Resolve final position (priority: midpoint > own-handle > object)
        let finalX, finalY;
        if (midSnap) {
            finalX = midSnap.snapX;
            finalY = midSnap.snapY;
        } else if (useOwn) {
            finalX = ownGuideX !== undefined ? ownSnapX : sx;
            finalY = ownGuideY !== undefined ? ownSnapY : sy;
        } else {
            finalX = sx;
            finalY = sy;
        }

        if (stage && stage.snapLayer) {
            stage.snapLayer.clearGuides();
            if (midSnap) {
                stage.snapLayer.drawPerpendicularGuide(
                    midSnap.midX, midSnap.midY, midSnap.segDx, midSnap.segDy,
                    midSnap.snapX, midSnap.snapY, '#FFFF00'
                );
            }
            if (useOwn) {
                if (ownGuideX !== undefined)
                    stage.snapLayer.drawVerticalGuide(
                        ownGuideX,
                        Math.min(cy, ownMatchY), Math.max(cy, ownMatchY),
                        '#FFFF00'
                    );
                if (ownGuideY !== undefined)
                    stage.snapLayer.drawHorizontalGuide(
                        ownGuideY,
                        Math.min(cx, ownMatchX), Math.max(cx, ownMatchX),
                        '#FFFF00'
                    );
            } else if (!midSnap && objR) {
                if (objR.guideX !== undefined)
                    stage.snapLayer.drawVerticalGuide(objR.guideX, objR.guideXyMin, objR.guideXyMax);
                if (objR.guideY !== undefined)
                    stage.snapLayer.drawHorizontalGuide(objR.guideY, objR.guideYxMin, objR.guideYxMax);
            }
            stage.snapLayer.flushGuides();
        }

        return { x: finalX, y: finalY };
    },

    _constrainAngle(x1, y1, x2, y2) {
        const dx = x2 - x1, dy = y2 - y1;
        const dist = Math.hypot(dx, dy);
        const angle = Math.atan2(dy, dx);
        const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        return { x: x1 + Math.cos(snapped) * dist, y: y1 + Math.sin(snapped) * dist };
    },

    _lineDrawGetContentPos() {
        const stage = this.getStage();
        const ptr = stage.getPointerPosition();
        if (!ptr) return null;
        return {
            x: (ptr.x - stage.x()) / stage.scaleX(),
            y: (ptr.y - stage.y()) / stage.scaleY(),
        };
    },

    _lineDrawMouseMove(evt) {
        if (!this.lineDrawingMode) return;

        let pos = this._lineDrawGetContentPos();
        if (!pos) return;

        // Always run snap so guides appear even before the first point is placed
        const snapped = this._snapDrawPoint(pos.x, pos.y);
        let cx = snapped.x, cy = snapped.y;

        // Preview segment only makes sense once there is at least one committed point
        if (this.lineDrawingPoints.length >= 2) {
            const lx = this.lineDrawingPoints[this.lineDrawingPoints.length - 2];
            const ly = this.lineDrawingPoints[this.lineDrawingPoints.length - 1];

            if (evt.evt?.shiftKey) {
                const c = this._constrainAngle(lx, ly, cx, cy);
                cx = c.x; cy = c.y;
            }

            this.linePreviewLine.points([lx, ly, cx, cy]);
            this.linePreviewLine.visible(true);
        }

        this.requestRender();
    },

    _lineDrawClick(e) {
        const now = Date.now();
        const isDbl = (now - this._lastLineClickTime) < 350;
        this._lastLineClickTime = now;
        if (isDbl) return; // dblclick handler will finish

        let pos = this._lineDrawGetContentPos();
        if (!pos) return;

        const snapped = this._snapDrawPoint(pos.x, pos.y);
        let cx = snapped.x, cy = snapped.y;

        if (e.evt?.shiftKey && this.lineDrawingPoints.length >= 2) {
            const lx = this.lineDrawingPoints[this.lineDrawingPoints.length - 2];
            const ly = this.lineDrawingPoints[this.lineDrawingPoints.length - 1];
            const c = this._constrainAngle(lx, ly, cx, cy);
            cx = c.x; cy = c.y;
        }

        this.lineDrawingPoints.push(cx, cy);

        // Add handle circle for the new committed point
        this.lineDrawingHandlesGroup.add(new Konva.Circle({
            id: uuidv4(),
            x: cx, y: cy,
            radius: 5,
            fill: '#FFFFFF',
            stroke: '#3B82F6',
            strokeWidth: 1.5,
            listening: false,
        }));

        // Update the committed-segments line
        this.lineDrawingLine.points([...this.lineDrawingPoints]);
        this.lineDrawingLine.visible(this.lineDrawingPoints.length >= 4);

        this.requestRender();
    },

    _lineDrawFinish() {
        if (this.lineDrawingPoints.length < 4) {
            this.cancelLineDrawing();
            return;
        }

        this._cleanupLineDrawing();

        const config = {
            obj: 'line',
            points: [...this.lineDrawingPoints],
            line_color: '#ffffff',
            line_width: 2,
            line_rounded: false,
            haspid: this.activePage.getNextId(),
            theme: this.theme,
            show_id: this.showId,
            layer: this,
        };

        const newLine = new HaspLine(config);
        if (newLine && this.lineDrawingContainer) {
            const item = this.lineDrawingContainer.addItem(newLine);
            if (item instanceof HaspObject) {
                newLine.moveToTop();
            }
        }

        this.lineDrawingPoints = [];
        this.lineDrawingContainer = null;

        if (newLine) {
            this.setSelectedNode(newLine);
            this.updatePageZeroOverlay('lineDrawFinish');
            this.layerCallback('add-object', newLine);
        }
    },

    cancelLineDrawing() {
        this._cleanupLineDrawing();
        this.lineDrawingPoints = [];
        this.lineDrawingContainer = null;
        this.layerCallback('line-drawing-cancel', null);
    },

    _cleanupLineDrawing() {
        this.lineDrawingMode = false;
        if (this.linePreviewLine) {
            this.linePreviewLine.destroy();
            this.linePreviewLine = null;
        }
        if (this.lineDrawingLine) {
            this.lineDrawingLine.destroy();
            this.lineDrawingLine = null;
        }
        if (this.lineDrawingHandlesGroup) {
            this.lineDrawingHandlesGroup.destroy();
            this.lineDrawingHandlesGroup = null;
        }
        if (this._lineDrawEscHandler) {
            document.removeEventListener('keydown', this._lineDrawEscHandler);
            this._lineDrawEscHandler = null;
        }
        const stage = this.getStage();
        if (stage) stage.container().style.cursor = '';
        if (stage && stage.snapLayer) stage.snapLayer.clearGuides();
    },
};

export default LineDrawingMixin;
